// Use the same approach as other modules
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class ClinicianService {
  constructor() {
    this.baseURL = API_BASE_URL;
    console.log('🏗️ ClinicianService initialized with baseURL:', this.baseURL);
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  // Get current user from localStorage
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Make authenticated API request
  async makeAuthenticatedRequest(url, options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const config = {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Could not validate credentials');
      }
      
      // Try to get detailed error message from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = Array.isArray(errorData.detail) 
            ? errorData.detail.map(err => err.msg || err).join(', ')
            : errorData.detail;
        }
      } catch (e) {
        // If we can't parse the error response, use the default message
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // ===== CLINICIAN PROFILE METHODS =====

  // Get current clinician profile
  async getCurrentClinicianProfile() {
    const url = `${this.baseURL}/clinicians/current/profile`;
    return this.makeAuthenticatedRequest(url);
  }

  // Update current clinician profile
  async updateCurrentClinicianProfile(clinicianData) {
    const url = `${this.baseURL}/clinicians/current/profile`;
    return this.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(clinicianData),
    });
  }

  // Get clinician by ID
  async getClinicianById(clinicianId) {
    const url = `${this.baseURL}/clinicians/${clinicianId}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get clinician by clinician_id
  async getClinicianByClinicianId(clinicianId) {
    const url = `${this.baseURL}/clinicians/clinician-id/${clinicianId}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get clinician by user_id
  async getClinicianByUserId(userId) {
    const url = `${this.baseURL}/clinicians/user/${userId}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get all clinicians
  async getAllClinicians(skip = 0, limit = 100) {
    const url = `${this.baseURL}/clinicians?skip=${skip}&limit=${limit}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Search clinicians
  async searchClinicians(searchTerm = '', specialty = '', skip = 0, limit = 100) {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search_term', searchTerm);
    if (specialty) params.append('specialty', specialty);
    params.append('skip', skip);
    params.append('limit', limit);
    
    const url = `${this.baseURL}/clinicians/search?${params.toString()}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get clinicians by clinic
  async getCliniciansByClinic(clinicId) {
    const url = `${this.baseURL}/clinicians/by-clinic/${clinicId}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get clinicians by clinic name
  async getCliniciansByClinicName(clinicName) {
    try {
      const url = `${this.baseURL}/clinicians/by-clinic-name/${encodeURIComponent(clinicName)}`;
      const clinicians = await this.makeAuthenticatedRequest(url);
      return clinicians || [];
    } catch (error) {
      console.error('Error fetching clinicians by clinic name:', error);
      return [];
    }
  }

  // Get clinician specialties
  async getClinicianSpecialties() {
    const url = `${this.baseURL}/clinicians/specialties`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get clinicians count
  async getCliniciansCount() {
    const url = `${this.baseURL}/clinicians/count`;
    return this.makeAuthenticatedRequest(url);
  }

  // ===== AVAILABILITY METHODS =====

  // Get month availability overview
  async getMonthAvailability(clinicianId, year, month) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/month?year=${year}&month=${month}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Set day availability
  async setDayAvailability(clinicianId, availabilityData) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/day`;
    return this.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(availabilityData),
    });
  }

  // Get day availability
  async getDayAvailability(clinicianId, dateStr) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/day/${dateStr}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get day time slots
  async getDayTimeSlots(clinicianId, dateStr) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/slots/${dateStr}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Get day slots (compatibility method for pages copied from doctor module)
  async getDaySlots(clinicianId, dateStr) {
    return this.getDayTimeSlots(clinicianId, dateStr);
  }

  // Replace day time slots
  async replaceDayTimeSlots(clinicianId, dateStr, slotsData) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/slots/${dateStr}`;
    return this.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(slotsData),
    });
  }

  // Replace day slots (compatibility method for pages copied from doctor module)
  async replaceDaySlots(clinicianId, dateStr, slots) {
    return this.replaceDayTimeSlots(clinicianId, dateStr, { date: dateStr, slots });
  }

  // Bulk set availability
  async bulkSetAvailability(clinicianId, bulkData) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/bulk`;
    return this.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(bulkData),
    });
  }

  // Set unavailability (compatibility method for pages copied from doctor module)
  async setUnavailability(clinicianId, dates, reason) {
    const bulkData = {
      dates,
      is_available: false,
      unavailability_reason: reason
    };
    return this.bulkSetAvailability(clinicianId, bulkData);
  }

  // Get available slots for booking
  async getAvailableSlots(clinicianId, startDate, endDate) {
    const url = `${this.baseURL}/clinicians/${clinicianId}/availability/available-slots?start_date=${startDate}&end_date=${endDate}`;
    return this.makeAuthenticatedRequest(url);
  }

  // Update slot status
  async updateSlotStatus(slotId, status, bookingId = null) {
    const params = new URLSearchParams({ status });
    if (bookingId) params.append('booking_id', bookingId);
    
    const url = `${this.baseURL}/clinicians/slots/${slotId}/status?${params.toString()}`;
    return this.makeAuthenticatedRequest(url, {
      method: 'PATCH',
    });
  }

  // ===== APPOINTMENTS AND BOOKINGS =====

  // Get clinical telehealth appointments for clinician
  async getClinicalAppointments(clinicianId, filters = {}) {
    try {
      // First get the clinician's clinics
      const clinics = await this.getClinicianClinics(clinicianId);
      const clinicNames = clinics.map(clinic => clinic.clinic_name);
      
      console.log('🏥 Clinician clinics:', clinicNames);
      
      // Get all appointments
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      
      // Filter for clinical telehealth appointments only
      params.append('appointment_type', 'clinical_telehealth');
      
      const url = `${this.baseURL}/patient-bookings/all?${params.toString()}`;
      const allAppointments = await this.makeAuthenticatedRequest(url);
      
      // Filter appointments by clinician's clinics
      const filteredAppointments = allAppointments.filter(appointment => {
        return clinicNames.includes(appointment.clinic_location);
      });
      
      console.log(`📅 Filtered ${filteredAppointments.length} appointments for clinician's clinics from ${allAppointments.length} total`);
      
      return filteredAppointments;
    } catch (error) {
      console.error('Error fetching clinical appointments:', error);
      return [];
    }
  }

  // Get today's clinical appointments
  async getTodaysClinicalAppointments(clinicianId) {
    const today = new Date().toISOString().split('T')[0];
    return this.getClinicalAppointments(clinicianId, {
      startDate: today,
      endDate: today
    });
  }

  // Get upcoming clinical appointments
  async getUpcomingClinicalAppointments(clinicianId, days = 7) {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + days);
    
    return this.getClinicalAppointments(clinicianId, {
      startDate: today.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  }

  // Get all appointments for clinician's clinics (not just clinical telehealth)
  async getClinicAppointments(clinicianId, filters = {}) {
    try {
      // First get the clinician's clinics
      const clinics = await this.getClinicianClinics(clinicianId);
      const clinicNames = clinics.map(clinic => clinic.clinic_name);
      
      console.log('🏥 Clinician clinics for all appointments:', clinicNames);
      
      // Get all appointments
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      
      // Don't filter by appointment type - get all appointments
      
      const url = `${this.baseURL}/patient-bookings/all?${params.toString()}`;
      const allAppointments = await this.makeAuthenticatedRequest(url);
      
      // Filter appointments by clinician's clinics
      const filteredAppointments = allAppointments.filter(appointment => {
        return clinicNames.includes(appointment.clinic_location);
      });
      
      console.log(`📅 Filtered ${filteredAppointments.length} total appointments for clinician's clinics from ${allAppointments.length} total`);
      
      return filteredAppointments;
    } catch (error) {
      console.error('Error fetching clinic appointments:', error);
      return [];
    }
  }

  // Get clinician bookings for a specific date (similar to doctor's getDoctorBookings)
  async getClinicianBookings(clinicianId, appointmentDate = null) {
    try {
      console.log('🔗 Clinician API Call - Clinician ID:', clinicianId, 'Date:', appointmentDate);
      
      // Get appointments for specific date using the new clinician-specific endpoint
      const params = new URLSearchParams();
      if (appointmentDate) {
        params.append('appointment_date', appointmentDate);
      }
      
      // Use the new clinician-specific bookings endpoint
      const url = `${this.baseURL}/clinicians/${clinicianId}/bookings${params.toString() ? '?' + params.toString() : ''}`;
      console.log('🌐 Full API URL (clinician endpoint):', url);
      
      const appointments = await this.makeAuthenticatedRequest(url);
      console.log('📡 Raw API response:', appointments);
      
      // The endpoint already filters by clinician's clinics and date, so no additional filtering needed
      console.log(`📅 Found ${appointments.length} appointments for clinician's clinics on ${appointmentDate}`);
      
      return appointments;
    } catch (error) {
      console.error('Error fetching clinician bookings:', error);
      return [];
    }
  }

  // Get today's appointments for clinician's clinics
  async getTodaysClinicAppointments(clinicianId) {
    const today = new Date().toISOString().split('T')[0];
    return this.getClinicianBookings(clinicianId, today);
  }

  // Get upcoming appointments for clinician's clinics
  async getUpcomingClinicAppointments(clinicianId, days = 7) {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + days);
    
    return this.getClinicAppointments(clinicianId, {
      startDate: today.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  }

  // ===== DASHBOARD STATISTICS =====

  // Get clinician dashboard statistics
  async getDashboardStats(clinicianId) {
    try {
      const [
        todayAppointments,
        upcomingAppointments,
        monthAvailability
      ] = await Promise.all([
        this.getTodaysClinicAppointments(clinicianId),
        this.getUpcomingClinicAppointments(clinicianId),
        this.getMonthAvailability(clinicianId, new Date().getFullYear(), new Date().getMonth() + 1)
      ]);

      return {
        todayAppointments: todayAppointments?.length || 0,
        upcomingAppointments: upcomingAppointments?.length || 0,
        totalAvailableSlots: monthAvailability?.days?.reduce((acc, day) => acc + day.available_slots_count, 0) || 0,
        availableDays: monthAvailability?.days?.filter(day => day.is_available)?.length || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        todayAppointments: 0,
        upcomingAppointments: 0,
        totalAvailableSlots: 0,
        availableDays: 0
      };
    }
  }

  // ===== UTILITY METHODS =====

  // Format appointment time
  formatAppointmentTime(timeString) {
    if (!timeString) return '';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  }

  // Format date
  formatDate(dateString) {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  // Check if current user is a clinician
  isCurrentUserClinician() {
    const user = this.getCurrentUser();
    return user?.role === 'clinician';
  }

  // Get clinician clinic information
  async getClinicianClinics(clinicianId) {
    try {
      const url = `${this.baseURL}/clinicians/${clinicianId}/clinics`;
      const response = await this.makeAuthenticatedRequest(url);
      return response.clinics || [];
    } catch (error) {
      console.error('Error fetching clinician clinics:', error);
      return [];
    }
  }
}

// Export a singleton instance
export const clinicianService = new ClinicianService();
export default ClinicianService;

